/**
 * Release-note lines in the reader's language (the user, 2026-10-28: "What's
 * new in MyRave should work as a template — the what's new just needs to FLAG
 * the line, and the text should come from templates").
 *
 * A `releases/<version>.md` FLAGS a line by writing its English; the words
 * shown come from `releases/i18n/<language>.json`, keyed by that English
 * exactly (`@myrave/shared` `releaseLines.ts`). Each language's table is its
 * OWN lazy chunk, loaded only by a page that shows release notes — What's
 * new, the version matrix's module history, the console's Versions — never
 * with the language's dictionary and never precached (`offline/swPlugin.ts`):
 * ~150 kB a language that a visitor at a festival does not need. English has
 * no table. A line a table lacks, or a table that failed to load, reads the
 * English — never nothing.
 */
import { useEffect, useState } from 'react';
import { languageOf, releaseLine, type Locale, type ReleaseTemplates } from '@myrave/shared';
import { useLocale } from '@/app/LocaleProvider';

const MODULES = import.meta.glob<ReleaseTemplates>('../../../releases/i18n/*.json', { import: 'default' });
const LOADERS: Partial<Record<string, () => Promise<ReleaseTemplates>>> = Object.fromEntries(
  Object.entries(MODULES).map(([path, load]) => [path.split('/').at(-1)!.replace(/\.json$/, ''), load]),
);
const LOADED: Partial<Record<string, ReleaseTemplates>> = {};

/** The languages with a table of release-note templates. */
export const RELEASE_LINE_LANGUAGES: readonly string[] = Object.keys(LOADERS);

/** A locale's templates if they are at hand now: `{}` for English (and a language with none), null while loading. */
export function releaseTemplatesNow(locale: Locale): ReleaseTemplates | null {
  const language = languageOf(locale);
  if (!LOADERS[language]) return {};
  return LOADED[language] ?? null;
}

/** Fetch a locale's templates; `{}` (so: the English) on a failure, never nothing. */
export async function loadReleaseTemplates(locale: Locale): Promise<ReleaseTemplates> {
  const language = languageOf(locale);
  const load = LOADERS[language];
  if (!load) return {};
  try {
    LOADED[language] ??= await load();
    return LOADED[language]!;
  } catch {
    return {};
  }
}

/**
 * `line(english)` — a flagged release line in the reader's language. While
 * the language's table is loading it is the English, so a page never shows a
 * blank line; it re-renders in the reader's words when the table arrives.
 */
export function useReleaseLine(): (english: string) => string {
  const { locale } = useLocale();
  const [templates, setTemplates] = useState<ReleaseTemplates | null>(() => releaseTemplatesNow(locale));
  useEffect(() => {
    const now = releaseTemplatesNow(locale);
    setTemplates(now);
    if (now) return undefined;
    let live = true;
    void loadReleaseTemplates(locale).then((t) => { if (live) setTemplates(t); });
    return () => { live = false; };
  }, [locale]);
  return (english: string) => releaseLine(english, templates);
}
